import React, { useEffect, useMemo, useState } from 'react'
import Card from '@veneer/core/dist/esm/scripts/card'
import lottie from 'lottie-web/build/player/lottie_light'
import classes from './CarouselPageModal.module.scss'
import { ANALYTICS, useAnalyticsAPI } from '../../../analytics'
import { formatNumber } from '../../../utils'
import LottieAnimation from '../LottieAnimation'

const ANIMATION_SELECTOR = 'instruction-'

const buildCards = (items) => {
  const cards = []
  items.forEach((item, i) => {
    let mediaElement

    if (item?.animation) {
      mediaElement = <LottieAnimation url={item?.animation} index={i} />
    } else if (item?.illustration) {
      if (item.illustration.endsWith('.json')) {
        mediaElement = <LottieAnimation url={item.illustration} index={i} />
      } else {
        mediaElement = (
          <img src={item.illustration} data-testid={`illustration-${i}`} />
        )
      }
    }

    const content = (
      <div className={classes.content} data-testid="content">
        <div className={classes.animation}>{mediaElement}</div>
        <div
          data-testid={`instruction-modal-${i}`}
          className={classes.instruction}
          dangerouslySetInnerHTML={{
            __html: item.instruction
          }}
        />
      </div>
    )
    const card = (
      <div className={classes.deck} key={i}>
        <div className={classes.card} data-testid="card">
          <Card>{content}</Card>
        </div>
      </div>
    )
    cards.push(card)
  })
  return cards
}

const Dots = ({ total, current }) => {
  return (
    <div className={classes.dots}>
      {new Array(total)
        .fill(null)
        .map((_, index) => index)
        .map((value) => {
          return (
            <div
              key={`dot_${value}`}
              data-testid={`dot-${value}-${current === value ? 'current' : ''}`}
              className={`${classes.dot} ${
                value === current ? classes.selected : ''
              }`}
            />
          )
        })}
    </div>
  )
}

const CarouselPageModalController = ({
  children,
  pageData,
  instruction,
  show,
  onClose = null,
  currentCardIndex = 0
}) => {
  const [currentCard, setCurrentCard] = useState(0)
  const [previousCard, setPreviousCard] = useState(0)
  const [totalCards, setTotalCards] = useState(0)
  const { trackScreenDisplay } = useAnalyticsAPI(
    ANALYTICS.MODALS[instruction],
    true
  )

  useEffect(() => {
    if (show && currentCardIndex > -1) {
      setCurrentCard(currentCardIndex)
    }
  }, [currentCardIndex, show])

  useEffect(() => {
    if (currentCard !== previousCard) {
      lottie.play(ANIMATION_SELECTOR + currentCard)
      lottie.pause(ANIMATION_SELECTOR + previousCard)
    }
  }, [currentCard, previousCard])

  const onCloseModal = () => {
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  const cards = useMemo(() => {
    if (pageData && pageData?.cards) {
      setTotalCards(pageData.cards.length)
      return buildCards(pageData.cards)
    }
  }, [pageData])

  useEffect(() => {
    if (show) {
      const screenIndex = currentCard + 1
      trackScreenDisplay({
        force: true,
        screenIndex: formatNumber(screenIndex, 2, '0')
      })
    }
  }, [currentCard, show])

  if (!pageData) return <></>

  return children({
    classes,
    pageData,
    cards,
    instruction,
    show,
    totalCards,
    currentCard,
    setCurrentCard,
    setPreviousCard,
    onCloseModal,
    Dots: <Dots total={totalCards} current={currentCard} />
  })
}

export default CarouselPageModalController
